import { Badge, Tooltip } from "antd";
import React from "react";
import {
  IngressStatusOutput,
  SiteConfigurationStatusOutput,
  ValidationStatusEnum,
} from "../../services/client/types.gen";
import { getStatusColor, getStatusText } from "./validationStatusFormat";

type ValidationStatusBadgeProps = {
  status?: ValidationStatusEnum;
  label?: string;
  ingressStatus?: IngressStatusOutput;
  configurationStatus?: SiteConfigurationStatusOutput;
};

const getIngressDetails = (
  ingressStatus?: IngressStatusOutput,
): { tooltipText?: string } => {
  const notReceiving =
    ingressStatus?.receiving_measurement_data?.not_receiving_data_from;

  if (!notReceiving || notReceiving.length === 0) {
    return {};
  }

  const count = notReceiving.length;
  const tooltipText = `Not receiving data from ${count} asset${count === 1 ? "" : "s"}: ${notReceiving.join(", ")}`;

  return { tooltipText };
};

const getConfigurationDetails = (
  configurationStatus?: SiteConfigurationStatusOutput,
): { displayText?: string; tooltipText?: string } => {
  // Check if site topology is missing entirely
  if (configurationStatus?.configuration_added?.status !== "OK") {
    return {
      displayText: "No topology",
      tooltipText: "Site topology not configured",
    };
  }

  // Check for missing assets in configuration
  const missingAssets =
    configurationStatus?.configuration_complete?.missing_assets;

  if (!missingAssets || missingAssets.length === 0) {
    return {};
  }

  const count = missingAssets.length;
  const tooltipText = `${count} asset${count === 1 ? "" : "s"} missing in configuration: ${missingAssets.join(", ")}`;

  return { tooltipText };
};

const ValidationStatusBadge: React.FC<ValidationStatusBadgeProps> = ({
  status,
  label,
  ingressStatus,
  configurationStatus,
}) => {
  const baseText = getStatusText(status);

  // Determine if this is an ingress or configuration status based on props
  const isIngress = ingressStatus !== undefined;
  const isConfiguration = configurationStatus !== undefined;

  let displayText = baseText;
  let tooltipText = label ? `${label}: ${baseText}` : baseText;

  if (isIngress) {
    const notReceiving =
      ingressStatus?.receiving_measurement_data?.not_receiving_data_from;
    const allMissing = notReceiving?.length === 1 && notReceiving[0] === "*";

    if (allMissing) {
      displayText = "No data";
      tooltipText = "Not receiving any data";
    } else {
      const details = getIngressDetails(ingressStatus);
      if (details.tooltipText) {
        tooltipText = details.tooltipText;
      }
    }
  }

  if (isConfiguration) {
    const details = getConfigurationDetails(configurationStatus);
    if (details.displayText) {
      displayText = details.displayText;
    }
    if (details.tooltipText) {
      tooltipText = details.tooltipText;
    }
  }

  return (
    <Tooltip title={tooltipText}>
      <Badge status={getStatusColor(status)} text={displayText} />
    </Tooltip>
  );
};

export default ValidationStatusBadge;
